import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { type DraggableProvidedDragHandleProps } from 'react-beautiful-dnd'
import { Actions, Box, Error, Icon, IconButton, Typography } from 'shared/ui'
import { type ReviewAccount } from 'entities/Review/model/ReviewAccount'
import styles from './styles.module.scss'

type IReviewAccountItemProps = {
  item: ReviewAccount
  onEdit: (account: ReviewAccount) => void
  onRemove: (account: ReviewAccount) => void
  dragHandleProps?: DraggableProvidedDragHandleProps | null
  isDragging?: boolean
}

export const ReviewAccountItem = observer(
  ({ item, onEdit, onRemove, dragHandleProps, isDragging }: IReviewAccountItemProps) => {
    const { customName, icon, url, urlLinkError } = item
    const errorMessage = urlLinkError && 'Invalid URL'

    return (
      <div className={styles.accountItemWrap}>
        <div
          className={classNames(styles.accountItem, {
            [styles.dragging]: isDragging,
            [styles.error]: !!errorMessage,
          })}
        >
          <div {...dragHandleProps} className={styles.dragWrap}>
            <Icon icon={'dotsDrag'} fontSize={12} color={'var(--content-primary-disabled)'} />
          </div>
          <Box padding={'8px'}>
            <Icon icon={icon} fontSize={16} />
          </Box>
          <Box paddingLeft={'4px'} paddingBottom={'3px'} minWidth={0}>
            <Typography ariaLabel={'Account'} variant={'button-small'} tag={'div'} ellipsis>
              {customName}
            </Typography>
          </Box>
          <Box display={'flex'} alignItems={'center'} padding={'7px 6px'} marginLeft={'auto'}>
            {errorMessage && (
              <Box marginRight={'8px'}>
                <Error type={'text'} error={urlLinkError && 'Invalid URL'} />
              </Box>
            )}
            <IconButton
              size={'small'}
              icon={'arrowRightUp'}
              color={'tertiary'}
              variant={'icon'}
              tooltipProps={{
                label: 'Open in new tab',
                placement: 'top',
              }}
              onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
              ariaLabel={'Open link'}
            />
            <Actions
              noVisibleActions
              iconButtonProps={{
                icon: 'moreHorizontal',
                size: 'small',
                iconProps: { fontSize: 16 },
              }}
              actions={[
                {
                  icon: 'edit',
                  text: 'Edit',
                  onAction: () => {
                    onEdit(item)
                  },
                },
                {
                  icon: 'delete',
                  text: 'Remove',
                  onAction: () => {
                    onRemove(item)
                  },
                },
              ]}
            />
          </Box>
        </div>
      </div>
    )
  }
)
